import { useState } from 'react';
import { motion } from 'framer-motion';
import { Utensils, CheckCircle, Palette, Truck, ShieldCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TovagliatoPage = () => {
  const [formData, setFormData] = useState({
    restaurantName: '',
    contactPerson: '',
    email: '',
    phone: '',
    tableCount: '',
    stylePreference: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/contact/tovagliato`, formData);
      setIsSubmitted(true);
    } catch (err) {
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen pt-20">
      {/* Hero */}
      <section className="relative py-20 md:py-32 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url(https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=1600&q=80)`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-text-primary/95 to-text-primary/70" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <span className="text-primary font-medium text-sm tracking-widest uppercase mb-4 block">
              Noleggio Tovagliato per Ristoranti
            </span>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">
              Smettila di dare ai tuoi clienti lo stesso tovagliato che danno nelle <span className="text-primary">catene</span>
            </h1>
            <p className="text-xl text-white/80 mb-4">
              Se non vuoi paragonarti a loro
            </p>
            <p className="text-2xl text-white font-medium mb-8">
              Alza la percezione del tuo ristorante con il tovagliato a noleggio di <span className="text-primary">Kilolava</span>
            </p>
            <Button 
              data-testid="tovagliato-hero-cta"
              size="lg"
              className="bg-primary hover:bg-primary-hover text-white px-8 py-6 text-lg rounded-full shadow-[0_20px_50px_rgb(233,30,99,0.3)]"
              onClick={() => document.getElementById('contact-tovagliato')?.scrollIntoView({ behavior: 'smooth' })}
            >
              Richiedi Informazioni
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-text-primary mb-6">
              Quanto spesso ti dicono<br />"da te si spende troppo"?
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6 mb-16">
            {[
              '"Là fanno la margherita a 5 euro"',
              '"Me lo faccio a casa spendo 10 euro e mangio pure di più"',
              '"Al fast food spendo lo stesso ma mangio il doppio"'
            ].map((quote, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-gray-100 rounded-2xl p-6 text-center"
              >
                <p className="text-xl text-text-secondary italic">{quote}</p>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-red-50 border border-red-200 rounded-3xl p-8 md:p-12 text-center max-w-4xl mx-auto"
          >
            <p className="text-lg md:text-xl text-text-secondary leading-relaxed mb-6">
              E dopo un po' ti viene voglia di iniziare a vendere panini con la mortadella a 4 euro
            </p>
            <p className="text-xl text-text-primary font-medium">
              Questo accade perché <strong className="text-primary">non ti impegni ad alzare la percezione</strong> 
              che i tuoi clienti hanno del tuo prodotto
              <br /><br />
              E semplicemente lo paragonano al fast food
            </p>
          </motion.div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-20 bg-primary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">
              Per questo sempre più locali stanno abbandonando<br />il tovagliato monouso per rivolgersi a <strong>Kilolava</strong>
            </h2>
            <p className="text-xl text-white/80 mb-12 max-w-3xl mx-auto">
              Con Kilolava non dovrai affrontare una spesa enorme per comprare il tuo tovagliato.
              Ti basterà <strong>noleggiare il nostro</strong>:
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            {[
              {
                icon: Palette,
                title: "Scegli il Colore",
                description: "Personalizza in base al tuo locale"
              },
              {
                icon: Utensils,
                title: "Scegli i Pezzi",
                description: "Tovaglie, tovaglioli, runner"
              },
              {
                icon: Truck,
                title: "Dicci le Quantità",
                description: "Noi consegniamo e ritiriamo"
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white/10 backdrop-blur-sm rounded-2xl p-8"
              >
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <item.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="font-heading text-xl font-bold mb-2">{item.title}</h3>
                <p className="text-white/80">{item.description}</p>
              </motion.div>
            ))}
          </div>

          <p className="text-2xl font-medium mb-8">
            Noi te lo consegniamo e te lo ritiriamo quando è sporco,<br />
            <strong>tu non dovrai pensare a nulla</strong>
          </p>

          <Button 
            data-testid="tovagliato-mid-cta"
            size="lg"
            className="bg-white text-primary hover:bg-white/90 px-8 py-6 text-lg rounded-full"
            onClick={() => document.getElementById('contact-tovagliato')?.scrollIntoView({ behavior: 'smooth' })}
          >
            Richiedi Informazioni
          </Button>
        </div>
      </section>

      {/* RABC Certificate Section */}
      <section className="py-20 bg-text-primary">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="w-20 h-20 bg-primary rounded-full flex items-center justify-center mx-auto mb-8">
              <ShieldCheck className="w-10 h-10 text-white" />
            </div>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6">
              E la cosa bella?
            </h2>
            <p className="text-xl text-white/80 mb-8">
              Grazie al nostro <strong className="text-primary">certificato RABC</strong> non rischierai 
              neanche spiacevoli sorprese in caso di controlli con i <strong>NAS</strong>
            </p>
            <Button 
              data-testid="tovagliato-rabc-cta"
              size="lg"
              className="bg-primary hover:bg-primary-hover text-white px-8 py-6 text-lg rounded-full"
              onClick={() => document.getElementById('contact-tovagliato')?.scrollIntoView({ behavior: 'smooth' })}
            >
              Richiedi Informazioni
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Contact Form */}
      <section id="contact-tovagliato" className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-text-primary mb-4">
              Richiedi Informazioni per <span className="text-primary">Tovagliato</span>
            </h2>
            <p className="text-text-secondary">Compila il modulo e ti ricontatteremo entro 24 ore</p>
          </motion.div>

          {isSubmitted ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-primary/5 border border-primary/20 rounded-3xl p-12 text-center"
            >
              <CheckCircle className="w-16 h-16 text-primary mx-auto mb-6" />
              <h3 className="font-heading text-2xl font-bold text-text-primary mb-4">
                Richiesta Inviata!
              </h3>
              <p className="text-text-secondary">Ti ricontatteremo presto.</p>
            </motion.div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6 bg-gray-50 rounded-3xl p-8 border border-gray-200">
              <div className="grid sm:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="restaurantName">Nome Ristorante *</Label>
                  <Input
                    data-testid="tovagliato-form-restaurant"
                    id="restaurantName"
                    name="restaurantName"
                    value={formData.restaurantName}
                    onChange={handleChange}
                    required
                    placeholder="Es. Ristorante Da Mario"
                    className="h-12 bg-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contactPerson">Referente *</Label>
                  <Input
                    data-testid="tovagliato-form-contact"
                    id="contactPerson"
                    name="contactPerson"
                    value={formData.contactPerson}
                    onChange={handleChange}
                    required
                    placeholder="Mario Rossi"
                    className="h-12 bg-white"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    data-testid="tovagliato-form-email"
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="email@esempio.com"
                    className="h-12 bg-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Telefono *</Label>
                  <Input
                    data-testid="tovagliato-form-phone"
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    placeholder="+39 333 1234567"
                    className="h-12 bg-white"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="tableCount">Numero Tavoli (circa)</Label>
                  <Input
                    data-testid="tovagliato-form-tables"
                    id="tableCount"
                    name="tableCount"
                    value={formData.tableCount}
                    onChange={handleChange}
                    placeholder="Es. 15"
                    className="h-12 bg-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="stylePreference">Preferenza Stile</Label>
                  <Select onValueChange={(value) => setFormData({...formData, stylePreference: value})}>
                    <SelectTrigger data-testid="tovagliato-form-style" className="h-12 bg-white">
                      <SelectValue placeholder="Seleziona stile" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="classico-bianco">Classico Bianco</SelectItem>
                      <SelectItem value="elegante-colorato">Elegante Colorato</SelectItem>
                      <SelectItem value="moderno-minimal">Moderno Minimal</SelectItem>
                      <SelectItem value="rustico">Rustico</SelectItem>
                      <SelectItem value="da-definire">Da Definire</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="message">Messaggio</Label>
                <Textarea
                  data-testid="tovagliato-form-message"
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Raccontaci del tuo ristorante e delle tue esigenze..."
                  rows={4}
                  className="bg-white"
                />
              </div>

              <Button
                data-testid="tovagliato-form-submit"
                type="submit"
                size="lg"
                disabled={isSubmitting}
                className="w-full bg-primary hover:bg-primary-hover text-white py-6 text-lg rounded-full"
              >
                {isSubmitting ? 'Invio in corso...' : 'Invia Richiesta'}
              </Button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
};

export default TovagliatoPage;
